<template>
    <div>
        <x-layout class="question-set">
            <x-flex xs2 offset-xs1>
                <h3 class="question-set-title">Insurers</h3>
            </x-flex>

            <x-flex xs6 class="question-set-questions">
                <div>
                    <x-container fluid pa-0>
                        <x-layout v-for="(insurer, index) in submission.insurers" :key="insurer.unique_key">
                            <x-flex xs12>
                                <x-container fluid class="question-set-questions-container" mb-3 :mt-5="index !== 0">
                                    <m-s-t-entry-insurer-info
                                        :available-insurers="getAvailableInsurers(insurer)"
                                        :insurer-data="insurer"
                                        :securities="securities"
                                        :currencies="currencies"
                                        :submission="submission"
                                        @deleteInsurer="deleteInsurer(insurer)"
                                        @resetInsurer="resetInsurer($event, insurer)"
                                    />
                                </x-container>
                            </x-flex>
                        </x-layout>

                        <x-layout>
                            <x-flex xs12 text-xs-center>
                                <x-btn
                                    class="wide my-3"
                                    color="secondary"
                                    @click="addInsurer"
                                    data-test="add_insurer"
                                >
                                    <i class="material-icons mr-2">add</i>

                                    Add {{ submission.insurers?.length > 0 ? 'another' : 'an' }} insurer
                                </x-btn>
                            </x-flex>
                        </x-layout>
                    </x-container>
                </div>
            </x-flex>
        </x-layout>

        <x-layout class="question-set">
            <x-flex xs2 offset-xs1>
                <h3 class="question-set-title">Status</h3>
            </x-flex>

            <x-flex xs6 class="question-set-questions">
                <x-container>
                    <x-layout>
                        <x-flex xs6>
                            <x-select
                                v-model="submission.status"
                                :items="riskStatuses"
                                class="required"
                                label="Risk status"
                                item-text="name"
                                item-value="id"
                                append-icon="keyboard_arrow_down"
                                :menu-props="{
                                    offsetY: true,
                                    maxHeight: 400
                                }"
                                :rules="[
                                    v => ruleRequired(v, 'Risk status')
                                ]"
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout>
                        <x-flex xs6>
                            <x-currency-text-field
                                v-model="submission.probability_of_binding"
                                label="Probability of binding"
                                placeholder=" "
                                suffix="%"
                                optional
                                :rules="[
                                    v => ruleNumeric(v, false, false, true),
                                    v => ruleLessThanOrEqualTo(v, 100, 'Probability of binding'),
                                    v => ruleGreaterThanOrEqualTo(v, 0, 'Probability of binding')
                                ]"
                            />
                        </x-flex>
                    </x-layout>
                </x-container>
            </x-flex>
        </x-layout>
    </div>
</template>

<script>
import MSTEntryInsurerInfo from "./MSTEntryInsurerInfo.vue";
import rules from "../../../mixins/rules";

export default {
    name: 'MSTEntryInsurerInformation',
    components: {MSTEntryInsurerInfo},
    props: {
        submission: {
            type: Object,
            required: true
        },
        insurerCompanies: {
            type: Array,
            required: true
        },
        currencies: {
            type: Array,
            required: true
        },
        securities: {
            type: Array,
            required: true
        }
    },
    mixins: [rules],
    data() {
        return {
            riskStatuses: [
                {
                    id: 'draft',
                    name: 'Draft'
                },
                {
                    id: 'in_the_market',
                    name: 'In the market'
                },
                {
                    id: 'not_taken_to_market',
                    name: 'Not taken to market'
                },
                {
                    id: 'ntu',
                    name: 'NTU'
                },
                {
                    id: 'declined',
                    name: 'Declined'
                },
                {
                    id: 'bound',
                    name: 'Bound outside of TepfinX'
                },
                {
                    id: 'nbi',
                    name: 'NBI'
                },
            ],
            nextKey: 1
        }
    },
    mounted() {
        if (this.submission.insurers?.length > 0) {
            this.submission.insurers?.forEach(ins => { ins.unique_key = ins.unique_key ? ins.unique_key : this.getUniqueKey() });
        }
    },
    methods: {
        getUniqueKey() {
            return this.nextKey++;
        },
        addInsurer() {
            this.submission.insurers.push(this.getEmptyInsurer());
        },
        deleteInsurer(insurer) {
            const index = this.submission.insurers.findIndex(ins => ins === insurer);
            this.submission.insurers.splice(index, 1);
        },
        resetInsurer(newCompanyId, insurer) {
            const index = this.submission.insurers.findIndex(ins => ins === insurer);
            Vue.set(this.submission.insurers, index, Object.assign(this.getEmptyInsurer(), {insurer_company_id: newCompanyId}))
        },
        getEmptyInsurer() {
            return {
                unique_key: this.getUniqueKey(),
                insurer_company_id: null,
                security: {},
                nbi_validity_period_days: 1,
            };
        },
        getAvailableInsurers(existingInsurer) {
            return this.insurerCompanies.filter(insurer => insurer.id === existingInsurer.insurer_company_id || !this.insurerIsSelected(insurer));
        },
        insurerIsSelected(insurer) {
            return this.submission.insurers.some(ins => ins.insurer_company_id === insurer.id)
        }
    },
    watch: {
        'submission.status'(status) {
            switch (status) {
                case 'draft': Vue.set(this.submission, 'probability_of_binding', null); break;
                case 'in_the_market': Vue.set(this.submission, 'probability_of_binding', 15); break;
                case 'not_taken_to_market': Vue.set(this.submission, 'probability_of_binding', 0); break;
                case 'ntu': Vue.set(this.submission, 'probability_of_binding', 0); break;
                case 'declined': Vue.set(this.submission, 'probability_of_binding', 0); break;
                case 'expired': Vue.set(this.submission, 'probability_of_binding', 0); break;
                case 'closed': Vue.set(this.submission, 'probability_of_binding', 0); break;
                case 'off_risk': Vue.set(this.submission, 'probability_of_binding', 0); break;
                case 'bound': Vue.set(this.submission, 'probability_of_binding', 100); break;
                case 'firm_ordered': Vue.set(this.submission, 'probability_of_binding', 90); break;
                case 'nbi': Vue.set(this.submission, 'probability_of_binding', 60); break;
            }
        }
    }
}
</script>

<style scoped lang="scss">

</style>
